import React, { useState, useEffect, useRef, useCallback } from 'react';
import './style.scss';

import { AppConfig } from '../api/appConfig';

type ReportTab = 'daily' | 'month' | 'year' | 'all';

interface CurrencyBreakdown {
  name: string;
  count: number;
  // rate: string;
}

/** Định dạng số: dấu phẩy phân cách hàng nghìn, dấu chấm phân cách thập phân (1,321,594) */
const formatNumber = (n: number | string): string => {
  const num = typeof n === 'string' ? parseFloat(n) || 0 : n;
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/** Định dạng USD: $18,570,177.23 */
const formatUSD = (n: number | string): string => {
  const num = typeof n === 'string' ? parseFloat(n) || 0 : n;
  const formatted = num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const trimmed = formatted.replace(/\.?0+$/, '');
  return `$${trimmed}`;
};

interface DailyBreakdownItem {
  currency: string;
  estTransactions: number;
  estVolume: string;
}

interface DailyReportData {
  date: string;
  estDailyUSD: string;
  estDailyOrders: number;
  breakdown: DailyBreakdownItem[];
}

interface MonthlyBreakdownItem {
  currency: string;
  transactions: number;
  volume: number;
}

interface MonthlyReportData {
  year: number;
  month: number;
  totalUSD: number;
  totalOrders: number;
  breakdown: MonthlyBreakdownItem[];
}

interface YearlyReportData {
  year: number;
  totalUSD: number;
  totalOrders: number;
  breakdown: MonthlyBreakdownItem[];
}

interface HistoryItem {
  monthKey: string;
  label: string;
  totalUSD: number;
  totalOrders: number;
}

interface AllTimeReportData {
  history: HistoryItem[];
  grandTotalUSD: number;
  grandTotalOrders: number;
  currentMonth: MonthlyReportData & { breakdown: MonthlyBreakdownItem[] };
}

type ReportData = DailyReportData | MonthlyReportData | YearlyReportData | AllTimeReportData | null;

interface Transaction {
  id: string;
  timestamp: string;
  provider: string;
  wallet_address: string;
  amount: number;
  token: string;
  fiatAmount: number;
  fiatCurrency: string;
  hash: string;
}

const CURRENCY_COLORS: Record<string, string> = {
  VND: 'cur-VND',
  THB: 'cur-THB',
  PHP: 'cur-PHP',
  BRL: 'cur-BRL',
  ARS: 'cur-ARS',
  USDT: 'cur-USDT',
  USDC: 'cur-USDC',
};

const getCurClass = (cur: string) => CURRENCY_COLORS[cur] || 'cur-N\\/A';

/** Credentials cho bảo mật nhẹ trang dashboard-log */
const DASHBOARD_LOG_AUTH = {
  storageKey: 'dashboard-log-auth',
  id: 'pwc336699',
  pass: 'Pwc$$$Money',
} as const;

/** Số có hiệu ứng ticker sàn chứng khoán khi thay đổi */
const FlipNumber = React.memo(({ value, rawValue, className }: { value: string; rawValue: number; className?: string }) => {
  const [tick, setTick] = useState<'up' | 'down' | null>(null);
  const prevRef = useRef({ value, rawValue });
  useEffect(() => {
    if (prevRef.current.value !== value) {
      const direction = rawValue > prevRef.current.rawValue ? 'up' : 'down';
      prevRef.current = { value, rawValue };
      setTick(direction);
      const t = setTimeout(() => setTick(null), 400);
      return () => clearTimeout(t);
    }
  }, [value, rawValue]);
  return (
    <span className={`stat-value-ticker ${tick ? `tick-${tick}` : ''} ${className ?? ''}`.trim()}>
      {value}
    </span>
  );
});

/** Định dạng số fiat: dấu phẩy phân cách hàng nghìn, dấu chấm thập phân (27.093, 177.000) */
const formatFiatAmount = (n: number): string => n.toLocaleString('en-US', { maximumFractionDigits: 0 });

/** Định dạng số crypto: dấu chấm thập phân (6.9904) */
const formatCryptoAmount = (n: number): string => Number(n).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 4 });

/** Cắt text ở giữa nếu vượt maxLen (ví dụ: 0x1234...abcd) */
const truncateText = (text: string, maxLen = 24): string => {
  if (!text) return '';
  if (text.length <= maxLen) return text;
  const head = Math.floor((maxLen - 3) / 2);
  const tail = maxLen - 3 - head;
  return `${text.slice(0, head)}...${text.slice(-tail)}`;
};

const TxnRow = React.memo(({ tx }: { tx: Transaction }) => (
  <div className="txn-row">
    <div className="txn-time">{tx.timestamp}</div>
    <div className="txn-provider-wrap">
      <span className={`cur-tag ${getCurClass(tx.fiatCurrency)}`}>{tx.fiatCurrency}</span>
      <span className="txn-provider">{tx.provider}</span>
    </div>
    <div className="txn-wallet" title={tx.wallet_address}>{tx.wallet_address}</div>
    <div className="txn-crypto">
      <span className="txn-amount">+{formatCryptoAmount(tx.amount)}</span>
      <span className="txn-token">{tx.token}</span>
    </div>
    <div className="txn-fiat">{formatFiatAmount(tx.fiatAmount)} {tx.fiatCurrency}</div>
    <div className="txn-hash">{tx.hash}</div>
  </div>
));



/** Login modal for dashboard-log light security */
const LoginModal = ({
  onSuccess,
}: {
  onSuccess: () => void;
}) => {
  const [id, setId] = useState('');
  const [pass, setPass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (id.trim() === DASHBOARD_LOG_AUTH.id && pass === DASHBOARD_LOG_AUTH.pass) {
      sessionStorage.setItem(DASHBOARD_LOG_AUTH.storageKey, '1');
      onSuccess();
    } else {
      setError('Invalid ID or password');
    }
  };

  return (
    <div
      className="login-gate-overlay"
      style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/login/Background.png)` }}
    >
      <div className="login-gate-modal">
        <div className="login-gate-header">
          <img src="/images/logo-collap.png" alt="PWC" className="login-gate-logo" />
          <h3>Sign in to view Dashboard</h3>
          <p>Enter your credentials to continue</p>
        </div>
        <form onSubmit={handleSubmit} className="login-gate-form">
          <div className="login-gate-field">
            <label htmlFor="dashboard-log-id">ID</label>
            <input
              id="dashboard-log-id"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="Enter ID"
              autoComplete="username"
              autoFocus
            />
          </div>
          <div className="login-gate-field">
            <label htmlFor="dashboard-log-pass">Password</label>
            <div className="login-gate-password-wrap">
              <input
                id="dashboard-log-pass"
                type={showPassword ? 'text' : 'password'}
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder="Enter password"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-gate-eye-btn"
                onClick={() => setShowPassword((p) => !p)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          {error && <div className="login-gate-error">{error}</div>}
          <button type="submit" className="login-gate-btn">
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
};

const LiveFeedSection = React.memo(({ feed, onClear }: { feed: Transaction[]; onClear: () => void }) => (
  <>
    <div className="feed-header">
      <div className="feed-title">LIVE TRANSACTION STREAM</div>
      {/* <button type="button" className="clear-btn" onClick={onClear}>
        Clear
      </button> */}
    </div>
    <div className="feed" id="feed">
      {feed.length === 0 ? (
        <div className="feed-empty">No transactions yet</div>
      ) : (
        feed.map((tx) => <TxnRow key={tx.id} tx={tx} />)
      )}
    </div>
  </>
));

const REPORT_APIS = {
  daily: AppConfig.GetReportDaily,
  month: AppConfig.GetReportMonth,
  year: AppConfig.GetReportYear,
  all: AppConfig.GetReportAll,
} as const;

const MONTH_NAMES: Record<number, string> = {
  1: 'January', 2: 'February', 3: 'March', 4: 'April', 5: 'May', 6: 'June',
  7: 'July', 8: 'August', 9: 'September', 10: 'October', 11: 'November', 12: 'December',
};

/** Chuyển breakdown từ Monthly API sang CurrencyBreakdown cho main dashboard */
const mapMonthlyBreakdown = (data: MonthlyReportData | null): CurrencyBreakdown[] => {
  if (!data?.breakdown?.length) return [];
  const total = data.breakdown.reduce((s, i) => s + i.transactions, 0);

  return data.breakdown.map((i) => ({
    name: i.currency,
    count: i.volume,
    // rate: total > 0 ? `${Number(((i.transactions / total) * 100).toFixed(2))}%` : '0%',
  }));
};

/** Daily tab: ESTIMATED DAILY VOLUME + Breakdown by Currency */
const ReportDailyView = ({
  data,
  formatNumber,
  formatUSD,
}: { data: DailyReportData; formatNumber: (n: number | string) => string; formatUSD: (n: number | string) => string }) => (
  <>
    <div className="report-summary-box report-daily-header">
      <h4>DAILY VOLUME ({data.date})</h4>
      <div className="summary-grid summary-daily">
        <div className="summary-item">
          <div className="label">Total Transactions / Day</div>
          <div className="value">{formatNumber(data.estDailyOrders)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Total Volume (USDT) / Day</div>
          <div className="value">{formatUSD(data.estDailyUSD)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Revenue (USDT)</div>
          <div className="value">{formatUSD((parseFloat(String(data.estDailyUSD).replace(/,/g, '')) || 0) * 0.03)}</div>
        </div>
      </div>
    </div>
    {/* <h4 className="report-table-title">Breakdown by Currency</h4>
    <table className="report-table">
      <thead>
        <tr>
          <th>CURRENCY</th>
          <th>TRANSACTIONS</th>
          <th>REVENUE (USDT)</th>
          <th>VALUE (USDT)</th>
        </tr>
      </thead>
      <tbody>
        {data.breakdown?.map((r) => (
          <tr key={r.currency}>
            <td>{r.currency}</td>
            <td>{formatNumber(r.estTransactions)}</td>
            <td>{formatUSD((parseFloat(String(r.estVolume).replace(/,/g, '')) || 0) * 0.03)}</td>
            <td>{formatUSD(r.estVolume)} USDT</td>
          </tr>
        ))}
      </tbody>
    </table> */}
  </>
);

/** Monthly tab: MONTHLY REPORT + Breakdown */
const ReportMonthlyView = ({
  data,
  formatNumber,
  formatUSD,
  monthNames,
}: { data: MonthlyReportData; formatNumber: (n: number | string) => string; formatUSD: (n: number | string) => string; monthNames: Record<number, string> }) => (
  <>
    <div className="report-summary-box report-monthly-header">
      <h4>MONTHLY REPORT — {monthNames[data.month] || data.month} {data.year}</h4>
      <div className="summary-grid summary-daily">
        <div className="summary-item">
          <div className="label">Total Transactions</div>
          <div className="value">{formatNumber(data.totalOrders)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Total Volume (USDT)</div>
          <div className="value">{formatUSD(data.totalUSD)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Revenue (USDT)</div>
          <div className="value">{formatUSD((data.totalUSD ?? 0) * 0.03)}</div>
        </div>
      </div>
    </div>
    {/* <h4 className="report-table-title">Breakdown by Currency</h4>
    <table className="report-table">
      <thead>
        <tr>
          <th>CURRENCY</th>
          <th>TRANSACTIONS</th>
          <th>REVENUE (USDT)</th>
          <th>VALUE (USDT)</th>
        </tr>
      </thead>
      <tbody>
        {data.breakdown?.map((r) => (
          <tr key={r.currency}>
            <td>{r.currency}</td>
            <td>{formatNumber(r.transactions)}</td>
            <td>{formatUSD((r.volume ?? 0) * 0.03)}</td>
            <td>{formatUSD(r.volume)}</td>
          </tr>
        ))}
      </tbody>
    </table> */}
  </>
);

/** Yearly tab: YEARLY REPORT + Breakdown */
const ReportYearlyView = ({
  data,
  formatNumber,
  formatUSD,
}: { data: YearlyReportData; formatNumber: (n: number | string) => string; formatUSD: (n: number | string) => string }) => (
  <>
    <div className="report-summary-box report-monthly-header">
      <h4>YEARLY REPORT — {data.year}</h4>
      <div className="summary-grid summary-daily">
        <div className="summary-item">
          <div className="label">Total Transactions</div>
          <div className="value">{formatNumber(data.totalOrders)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Total Volume (USDT)</div>
          <div className="value">{formatUSD(data.totalUSD)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Revenue (USDT)</div>
          <div className="value">{formatUSD((data.totalUSD ?? 0) * 0.03)}</div>
        </div>
      </div>
    </div>
    {/* <h4 className="report-table-title">Breakdown by Currency</h4>
    <table className="report-table">
      <thead>
        <tr>
          <th>CURRENCY</th>
          <th>TRANSACTIONS</th>
          <th>REVENUE (USDT)</th>
          <th>VALUE (USDT)</th>
        </tr>
      </thead>
      <tbody>
        {data.breakdown?.map((r) => (
          <tr key={r.currency}>
            <td>{r.currency}</td>
            <td>{formatNumber(r.transactions)}</td>
            <td>{formatUSD((r.volume ?? 0) * 0.03)}</td>
            <td>{formatUSD(r.volume)}</td>
          </tr>
        ))}
      </tbody>
    </table> */}
  </>
);

/** All-Time tab: TOTAL ALL-TIME + Monthly History + Current Month Breakdown */
const ReportAllTimeView = ({
  data,
  formatNumber,
  formatUSD,
  monthNames,
}: { data: AllTimeReportData; formatNumber: (n: number | string) => string; formatUSD: (n: number | string) => string; monthNames: Record<number, string> }) => (
  <>
    <div className="report-summary-box report-alltime-header">
      <h4 className="report-alltime-title">TOTAL ALL-TIME</h4>
      <div className="summary-grid summary-daily">
        <div className="summary-item">
          <div className="label">Transactions</div>
          <div className="value">{formatNumber(data.grandTotalOrders)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Volume (USDT)</div>
          <div className="value">{formatUSD(data.grandTotalUSD)}</div>
        </div>
        <div className="summary-item">
          <div className="label">Revenue (USDT)</div>
          <div className="value">{formatUSD((data.grandTotalUSD ?? 0) * 0.03)}</div>
        </div>
      </div>
    </div>
    <h4 className="report-table-title">Monthly History</h4>
    <table className="report-table">
      <thead>
        <tr>
          <th>MONTH</th>
          <th>TRANSACTIONS</th>
          <th>REVENUE (USDT)</th>
          <th>VALUE (USDT)</th>
        </tr>
      </thead>
      <tbody>
        {data.history?.map((h) => (
          <tr key={h.monthKey}>
            <td>{h.label}</td>
            <td>{formatNumber(h.totalOrders)}</td>
            <td>{formatUSD((h.totalUSD ?? 0) * 0.03)}</td>
            <td>{formatUSD(h.totalUSD)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {/* <h4 className="report-table-title">Current Month Breakdown</h4>
    <table className="report-table">
      <thead>
        <tr>
          <th>CURRENCY</th>
          <th>TRANSACTIONS</th>
          <th>REVENUE (USDT)</th>
          <th>VALUE (USDT)</th>
        </tr>
      </thead>
      <tbody>
        {data.currentMonth?.breakdown?.map((r) => (
          <tr key={r.currency}>
            <td>{r.currency}</td>
            <td>{formatNumber(r.transactions)}</td>
            <td>{formatUSD((r.volume ?? 0) * 0.03)}</td>
            <td>{formatUSD(r.volume)}</td>
          </tr>
        ))}
      </tbody>
    </table> */}
  </>
);

/** Chuyển dữ liệu từ SSE sang Transaction */
const mapToTransaction = (raw: Record<string, unknown>): Transaction => {
  const hash = String(raw.hash ?? raw.txHash ?? raw.hashTx ?? '0x...');
  return {
    id: String(raw.id ?? raw.txHash ?? crypto.randomUUID()),
    timestamp: String(raw.timestamp ?? raw.time ?? raw.createdAt ?? '—'),
    provider: String(raw.provider ?? raw.providerName ?? 'N/A'),
    wallet_address: String(raw.wallet_address ?? raw.walletAddress ?? raw.email ?? ''),
    amount: Number(raw.amount ?? 0),
    token: String(raw.token ?? 'USDT'),
    fiatAmount: Number(raw.fiatAmount ?? 0),
    fiatCurrency: String(raw.fiatCurrency ?? raw.currency ?? raw.currencyCode ?? 'N/A'),
    hash: hash.length > 16 ? `${hash.slice(0, 4)}...xxxx...${hash.slice(-4)}` : hash,
  };
};

/** Kiểm tra response CheckStatusLive có phải live không */
const isStatusLive = (data: unknown): boolean => {
  const d = data && typeof data === 'object' ? data as Record<string, unknown> : {};
  const status = String(d.status ?? d.live ?? (d.data as Record<string, unknown>)?.status ?? (d.data as Record<string, unknown>)?.live ?? '').toLowerCase();
  if (status === 'true' || status === 'live' || status === '1') return true;
  if (status === 'false' || status === 'offline' || status === '0') return false;
  return Boolean(d.live ?? (d.data as Record<string, unknown>)?.live);
};

const DashboardLog = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    sessionStorage.getItem(DASHBOARD_LOG_AUTH.storageKey) === '1'
  );
  const [isLive, setIsLive] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);
  const userRequestedOfflineRef = useRef(false);

  const toggleLive = () => {
    if (isLive) {
      userRequestedOfflineRef.current = true;
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      setIsLive(false);
    } else {
      userRequestedOfflineRef.current = false;
      connectSSE();
    }
  };
  const [feed, setFeed] = useState<Transaction[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeReport, setActiveReport] = useState<ReportTab>('daily');
  const [reportData, setReportData] = useState<ReportData>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  /** Nguồn chung cho dashboard stats + modal tab Month; được cập nhật bởi setInterval 2s */
  const [monthlyReportData, setMonthlyReportData] = useState<MonthlyReportData | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const feedBufferRef = useRef<Transaction[]>([]);
  const flushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastStatusRef = useRef<boolean | null>(null);
  const emptyRecoveryAttemptedRef = useRef(false);
  const lastFeedUpdateRef = useRef(Date.now());

  const flushFeedBuffer = useCallback(() => {
    if (feedBufferRef.current.length === 0) return;
    const batch = feedBufferRef.current;
    feedBufferRef.current = [];
    lastFeedUpdateRef.current = Date.now();
    emptyRecoveryAttemptedRef.current = false;
    React.startTransition(() => {
      setFeed((prev) => [...batch, ...prev].slice(0, 100));
    });
  }, []);

  const pushToFeed = useCallback((tx: Transaction) => {
    feedBufferRef.current.push(tx);
  }, []);

  const connectSSE = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    const token = sessionStorage.getItem('access-token-with-crypto');
    const url = AppConfig.GetStreamUrl();
  
    const controller = new AbortController();
    abortRef.current = controller;
    setStreamError(null);
    userRequestedOfflineRef.current = false;

    fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        if (!res.body) throw new Error('No stream body');
      
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        setIsLive(true);
        lastFeedUpdateRef.current = Date.now();
        flushIntervalRef.current = setInterval(flushFeedBuffer, 400);
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (flushIntervalRef.current) {
              clearInterval(flushIntervalRef.current);
              flushIntervalRef.current = null;
            }
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.startsWith('data:')) {
              try {
                const json = line.slice(5).trim();
                if (!json || json === '[DONE]') continue;
                const parsed = JSON.parse(json) as Record<string, unknown>;
              
                const tx = mapToTransaction(parsed);
        
                pushToFeed(tx);
              } catch (error) {
                console.warn('[Stream] parse error line:', line, error);
              }
            }
          }
        }
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (flushIntervalRef.current) {
          clearInterval(flushIntervalRef.current);
          flushIntervalRef.current = null;
        }
        setStreamError(err?.message ?? 'Stream error');
        setIsLive(false);
        console.error('[Stream] connection error:', err);
      });
  }, [pushToFeed, flushFeedBuffer]);

  useEffect(() => {
    connectSSE();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (flushIntervalRef.current) {
        clearInterval(flushIntervalRef.current);
        flushIntervalRef.current = null;
      }
    };
  }, [connectSSE]);

  /** Khi feed rỗng HOẶC data không thay đổi sau 2s: gọi DisableStream rồi EnableStream để lấy lại data */
  useEffect(() => {
    if (!isLive || emptyRecoveryAttemptedRef.current) return;

    const checkAndRecover = () => {
      if (!emptyRecoveryAttemptedRef.current && Date.now() - lastFeedUpdateRef.current >= 2000) {
        emptyRecoveryAttemptedRef.current = true;
        AppConfig.DisableStream()
          .then(() => AppConfig.EnableStream())
          .catch(() => {})
          .finally(() => {
            lastFeedUpdateRef.current = Date.now();
          });
      }
    };

    const timer = setTimeout(checkAndRecover, 2000);
    const interval = setInterval(checkAndRecover, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [isLive]);


  useEffect(() => {
    const checkStatus = () => {
      AppConfig.CheckStatusLive()
        .then((res) => {
          const live = res.data?.isRunning;
          const prev = lastStatusRef.current;
          lastStatusRef.current = live;

          if (live) {
            userRequestedOfflineRef.current = false;
            setIsLive((s) => (s === live ? s : live));
          } else if (!userRequestedOfflineRef.current) {
            const wasLive = prev === true;
            setIsLive((s) => (s === false ? s : false));
            if (wasLive) {
              setStreamError('Stream offline, reconnecting...');
              connectSSE();
            }
          }
        })
        .catch(() => {
          if (!userRequestedOfflineRef.current) {
            const prev = lastStatusRef.current;
            lastStatusRef.current = false;
            setIsLive((s) => (s === false ? s : false));
            if (prev === true) {
              setStreamError('Status check failed, reconnecting...');
              connectSSE();
            }
          }
        });
    };
    checkStatus();
    const id = setInterval(checkStatus, 3000);
    return () => clearInterval(id);
  }, [connectSSE]);

  useEffect(() => {
    const loadMonthly = () => {
      AppConfig.GetReportMonth()
        .then((res) => setMonthlyReportData((res.data ?? res) as MonthlyReportData))
        .catch(() => { /* bỏ qua lỗi */ });
    };
    loadMonthly();
    const id = setInterval(loadMonthly, 500);
    return () => clearInterval(id);
  }, []);

  /** Chỉ chạy khi mở modal hoặc đổi tab — không phụ thuộc monthlyReportData */
  useEffect(() => {
    if (!modalOpen) return;
    if (activeReport === 'month') {
      setReportData(monthlyReportData);
      setReportLoading(false);
    } else {
      setReportLoading(true);
      setReportError(null);
      REPORT_APIS[activeReport]()
        .then((res) => setReportData((res.data ?? res) as ReportData))
        .catch((err) => setReportError(err?.message ?? 'Failed to load report'))
        .finally(() => setReportLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, activeReport]);

  /** Chỉ đồng bộ reportData khi đang ở tab Month và monthlyReportData thay đổi */
  useEffect(() => {
    if (modalOpen && activeReport === 'month' && monthlyReportData) {
      setReportData(monthlyReportData);
    }
  }, [modalOpen, activeReport, monthlyReportData]);

  const handleTabChange = (tab: ReportTab) => setActiveReport(tab);

  const handleClearFeed = useCallback(() => {
    feedBufferRef.current = [];
    setFeed([]);
  }, []);
  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleLogout = () => {
    sessionStorage.removeItem(DASHBOARD_LOG_AUTH.storageKey);
    setIsAuthenticated(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="dashboard-live-feed">
        <LoginModal onSuccess={() => setIsAuthenticated(true)} />
      </div>
    );
  }

  return (
    <div className="dashboard-live-feed">
      <header className="dashboard-header">
        <div className="logo">
          <img src="/images/logo-collap.png" alt="PWC" className="logo-img" />
          {/* <div
            className={`logo-dot ${isLive ? 'live' : ''}`}
            style={!isLive ? { background: 'var(--muted)', boxShadow: 'none' } : undefined}
          /> */}
          <div className="logo-text">PWC <span>Live Feed</span></div>
        </div>
        <div className="controls">
          {/* {streamError && <span className="stream-error" title={streamError}>{streamError}</span>} */}
          <button
            type="button"
            className={`status-badge ${isLive ? 'live' : ''}`}
            // onClick={toggleLive}
          >
            ● {isLive ? 'LIVE' : 'OFFLINE'}
          </button>
          <button type="button" className="btn btn-report" onClick={openModal}>
            📊 Reports
          </button>
          <button type="button" className="btn btn-logout" onClick={handleLogout} aria-label="Logout" title="Logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </header>

      <div className="stats-bar">
        <div className="stat-card">
          <div className="stat-label">TOTAL TRANSACTIONS</div>
          <FlipNumber value={formatNumber(monthlyReportData?.totalOrders ?? 0)} rawValue={monthlyReportData?.totalOrders ?? 0} className="green" />
          <div className="stat-sub">this month</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">VOLUME (USDT)</div>
          <FlipNumber value={formatNumber(monthlyReportData?.totalUSD ?? 0)} rawValue={monthlyReportData?.totalUSD ?? 0} className="purple" />
          <div className="stat-sub">this month</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">REVENUE (USDT)</div>
          <FlipNumber
            value={formatNumber((monthlyReportData?.totalUSD ?? 0) * 0.03)}
            rawValue={(monthlyReportData?.totalUSD ?? 0) * 0.03}
            className="accent"
          />
          <div className="stat-sub">this month</div>
        </div>
      </div>

      {/* <div className="breakdown">
        {mapMonthlyBreakdown(monthlyReportData).map((cur) => (
          <div key={cur.name} className="cur-card">
            <div className="cur-name">{cur.name} (USDT)</div>
            <div className="cur-count">{formatNumber(cur.count)}</div>
          </div>
        ))}
      </div> */}

      <div className="feed-section">
        <LiveFeedSection feed={feed} onClear={handleClearFeed} />
      </div>

     

      <div className={`modal-overlay ${modalOpen ? 'active' : ''}`} onClick={closeModal} onKeyDown={() => {}} role="button" tabIndex={0}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()} onKeyDown={() => {}} role="dialog" tabIndex={0}>
          <div className="modal-header">
            <div className="modal-title">Transaction Reports</div>
            <button type="button" className="modal-close" onClick={closeModal} aria-label="Close">✕</button>
          </div>
          <div className="modal-tabs">
            {(['daily', 'month', 'year', 'all'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={`modal-tab ${activeReport === tab ? 'active' : ''}`}
                onClick={() => handleTabChange(tab)}
              >
                {tab === 'daily' && 'Daily'}
                {tab === 'month' && 'Monthly'}
                {tab === 'year' && 'Yearly'}
                {tab === 'all' && 'All-Time'}
              </button>
            ))}
          </div>
          <div className="modal-body">
            {reportError && <div className="stream-error" style={{ marginBottom: 12 }}>{reportError}</div>}
            {reportLoading ? (
              <div style={{ padding: 24, textAlign: 'center' }}>Loading...</div>
            ) : (
              <>
                {activeReport === 'daily' && reportData && 'estDailyOrders' in reportData && (
                  <ReportDailyView data={reportData as DailyReportData} formatNumber={formatNumber} formatUSD={formatUSD} />
                )}
                {activeReport === 'month' && reportData && 'month' in reportData && (
                  <ReportMonthlyView data={reportData as MonthlyReportData} formatNumber={formatNumber} formatUSD={formatUSD} monthNames={MONTH_NAMES} />
                )}
                {activeReport === 'year' && reportData && 'year' in reportData && !('month' in reportData) && !('history' in reportData) && (
                  <ReportYearlyView data={reportData as YearlyReportData} formatNumber={formatNumber} formatUSD={formatUSD} />
                )}
                {activeReport === 'all' && reportData && 'history' in reportData && (
                  <ReportAllTimeView data={reportData as AllTimeReportData} formatNumber={formatNumber} formatUSD={formatUSD} monthNames={MONTH_NAMES} />
                )}
                {!reportData && !reportLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>No data</div>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLog;
